'use client';

import { getValidatedQueryParameters } from '@svs/nova-poolbetting/core/lib/client';
import type { ModalTypes, QueryStringSchema } from '@svs/nova-poolbetting/schema/input';
import { useMediaQueryAbove } from '@svs/ui/media-query/react';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import { useEffect, useRef } from 'react';

type ModalToDesktopStateType = {
  [K in keyof QueryStringSchema]?: { key: K; value: QueryStringSchema[K] };
};

// mapping the available modals to equivalent searchparameters in desktop view.
const MODAL_TO_DESKTOP_STATE: Partial<Record<ModalTypes, ModalToDesktopStateType[keyof QueryStringSchema]>> = {
  draw: { key: 'drawOpen', value: true },
  gameGuide: { key: 'tab', value: 'spela' },
};

type DesktopToModalStateType = {
  [K in keyof QueryStringSchema]?: { key: 'modal'; value: Exclude<QueryStringSchema['modal'], undefined> };
};

// mapping the available searchparameters to equivalent modals.
const DESKTOP_TO_MODAL_STATE: Partial<Record<keyof QueryStringSchema, DesktopToModalStateType['modal']>> = {
  drawOpen: { key: 'modal', value: 'draw' },
  tab: { key: 'modal', value: 'gameGuide' },
};

function getSmallScreenParams(params: URLSearchParams): URLSearchParams | undefined {
  let isModified = false;
  for (const [desktopKey, modalMapping] of Object.entries(DESKTOP_TO_MODAL_STATE)) {
    const desktopParamValue = params.get(desktopKey);

    // For boolean parameters (drawOpen), only convert if the value is "true"
    const shouldConvertToModal = desktopKey === 'drawOpen' && desktopParamValue === 'true';

    if (shouldConvertToModal) {
      params.delete(desktopKey);
      params.set(modalMapping.key, modalMapping.value);
      isModified = true;
    }
  }

  return isModified ? params : undefined;
}

function getLargeScreenParams(params: URLSearchParams): URLSearchParams | undefined {
  const { modal: modalParamValue } = getValidatedQueryParameters('modal')(params);

  // making sure theres a modaltype set in the url and it has an equivalent desktop state.
  if (modalParamValue && MODAL_TO_DESKTOP_STATE[modalParamValue]) {
    params.delete('modal');
    const { key, value } = MODAL_TO_DESKTOP_STATE[modalParamValue];
    params.set(key, String(value));
  } else {
    return undefined;
  }

  return params;
}

/**
 * Component that watches for changes in screensize and applies the correct queryparameters.
 * The correct states are defined in {@link MODAL_TO_DESKTOP_STATE} and {@link DESKTOP_TO_MODAL_STATE} Records.
 *
 * @returns nothing.
 */
export default function ResponsiveAutoNavigation(): undefined {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const isLargeScreen = useMediaQueryAbove('md');
  const router = useRouter();
  const previousScreenSize = useRef<boolean | undefined>(undefined);

  useEffect(() => {
    // Skip on initial render to avoid flash of converted params when screen size is still unknown
    if (previousScreenSize.current === undefined) {
      previousScreenSize.current = isLargeScreen;
      return;
    }

    // Only apply conversions if screen size actually changed
    if (previousScreenSize.current === isLargeScreen) {
      return;
    }

    previousScreenSize.current = isLargeScreen;

    const existingSearchParams = new URLSearchParams(searchParams);

    const modifiedSearchParams = isLargeScreen
      ? getLargeScreenParams(existingSearchParams)
      : getSmallScreenParams(existingSearchParams);

    if (modifiedSearchParams) {
      const newUrl = `${pathname}?${modifiedSearchParams}`;
      router.replace(newUrl);
    }
  }, [searchParams, isLargeScreen, router, pathname]);

  return undefined;
}
